import type { ChatMessage, ChatMessagesResponse, MessageFeedbackState } from "@/types/chat";

const MOCK_DELAY = 1000;

const mockMessages: ChatMessage[] = [
  {
    id: 2,
    chat_id: 1,
    content: "I need help understanding how to use React hooks, particularly useEffect. Can you explain?",
    source: "from_human",
    created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: "",
    meta_data: null,
    selected_document_ids: [],
  },
  {
    id: 3,
    chat_id: 1,
    content:
      "I'll explain useEffect with a simple example:\n\n```jsx\nuseEffect(() => {\n  // This runs after every render\n  console.log('Component rendered');\n  \n  return () => {\n    // This cleanup runs before the next effect\n    console.log('Cleanup');\n  };\n}, []); // Empty deps array = only runs once\n```\n\nThe useEffect hook lets you perform side effects in function components. It runs after render and can clean up before the next effect runs.",
    source: "from_bot",
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: "",
    meta_data: null,
    selected_document_ids: [],
  },
  {
    id: 4,
    chat_id: 1,
    content: "That makes sense! What about the dependencies array? When should I add dependencies to it?",
    source: "from_human",
    created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    updated_at: "",
    meta_data: null,
    selected_document_ids: [],
  },
  {
    id: 5,
    chat_id: 1,
    content:
      "Add dependencies to the array when your effect uses values from props or state. For example:\n\n```jsx\nuseEffect(() => {\n  // This effect depends on userId\n  fetchUserData(userId);\n}, [userId]); // Re-run when userId changes\n```\n\nIf you omit a dependency that the effect uses, you might have stale data or infinite loops.",
    source: "from_bot",
    created_at: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
    updated_at: "",
    meta_data: null,
    selected_document_ids: [],
  },
  {
    id: 6,
    chat_id: 1,
    content: "Perfect! This really helped me understand useEffect better. Thank you!",
    source: "from_human",
    created_at: new Date().toISOString(),
    updated_at: "",
    meta_data: null,
    selected_document_ids: [],
  },
];

export async function getMessages(conversationId: number, page = 1, limit = 50): Promise<ChatMessagesResponse> {
  await new Promise((resolve) => setTimeout(resolve, MOCK_DELAY));

  const messages = mockMessages.filter((m) => m.chat_id === conversationId);
  const start = (page - 1) * limit;
  const end = start + limit;
  const data = messages.slice(start, end);

  return {
    data,
    meta: {
      currentPage: page,
      totalPages: Math.ceil(messages.length / limit),
      totalItems: messages.length,
    },
  };
}

export async function fetchChatConfiguration(chatId: number) {
  try {
    console.log(`Fetching chat configuration for chat ${chatId}`);
    
    const response = await fetch(`/api/v1/chats/${chatId}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Server responded with ${response.status}: ${errorText}`);
      throw new Error(`Failed to fetch chat: ${response.statusText}`);
    }

    const chat = await response.json();
    console.log('Chat configuration fetched:', chat.chat_configuration || chat.configuration);
    return chat.chat_configuration || chat.configuration || { currently_selected_documents: [] };
  } catch (error) {
    console.error('Error fetching chat configuration:', error);
    throw error;
  }
}

export async function updateChatConfiguration(chatId: number, currentlySelectedDocuments: number[]) {
  try {
    console.log(`Updating chat ${chatId} configuration with documents:`, currentlySelectedDocuments);
    
    const response = await fetch(`/api/v1/chats/${chatId}/chat_configuration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
      },
      body: JSON.stringify({
        chat_configuration: {
          currently_selected_documents: currentlySelectedDocuments
        }
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Server responded with ${response.status}: ${errorText}`);
      throw new Error(`Failed to update chat configuration: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Chat configuration updated successfully:', result);
    return result;
  } catch (error) {
    console.error('Error updating chat configuration:', error);
    throw error;
  }
}

export async function getResearchDocumentDefault(municipalityId: number) {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/research_document_default`, {
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load research document default: ${response.statusText}`);
  }

  return response.json() as Promise<{ document_ids: number[] | null }>;
}

export async function updateResearchDocumentDefault(municipalityId: number, documentIds: number[]) {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/research_document_default`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
    },
    body: JSON.stringify({
      document_ids: documentIds,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update research document default: ${response.statusText}`);
  }

  return response.json() as Promise<{ document_ids: number[] }>;
}

export async function deleteResearchDocumentDefault(municipalityId: number) {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/research_document_default`, {
    method: "DELETE",
    headers: {
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to clear research document default: ${response.statusText}`);
  }
}

export async function getDocumentsForMunicipality(municipalityId: number) {
  try {
    console.log(`Fetching documents for municipality ${municipalityId}`);
    
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
    const response = await fetch(`/api/v1/municipalities/${municipalityId}/documents`, {
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Server responded with ${response.status}: ${errorText}`);
      throw new Error(`Failed to fetch documents: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
}

export type DocumentCatalogList = "research_assistant" | "file_center";

export interface DocumentCatalogEntry {
  id: number;
  name: string;
  description?: string | null;
  status: string;
  added_at: string;
  /** constituent_portal row. The Connect badge, not the checkbox. */
  connect?: boolean;
  /** Checkbox: on Connect, or waiting to be placed after processing. */
  connect_selected?: boolean;
  file_center?: boolean;
  /** Checkbox: on Research Assistant, or waiting to be placed after processing. */
  research_assistant?: boolean;
  municipality_document_group_id?: number | null;
}

export interface DocumentCatalogPlacement {
  id?: number;
  file_center: boolean;
  connect: boolean;
  research_assistant: boolean;
  municipality_document_group_id: number | null;
}

export type DocumentIngestStatus = "received" | "processing" | "ready" | "needs_attention";

export interface DocumentCatalogInFlightEntry {
  id: number;
  name: string;
  status: string;
  added_at: string;
  ingest_status: DocumentIngestStatus;
  michael_pending: boolean;
}

export interface DocumentCatalogQuery {
  list?: DocumentCatalogList;
  q?: string;
  recent?: boolean;
  page?: number;
  inFlightPage?: number;
}

export interface DocumentCatalogPagination {
  current_page: number;
  total_pages: number;
  per_page: number;
  total_count: number;
}

export interface DocumentCatalogFolder {
  id: number;
  name: string;
  position: number;
  parent_id?: number | null;
}

export interface DocumentCatalogResponse {
  list: DocumentCatalogList;
  counts: Record<DocumentCatalogList, number>;
  documents: DocumentCatalogEntry[];
  in_flight?: DocumentCatalogInFlightEntry[];
  in_flight_pagination?: DocumentCatalogPagination;
  folders?: DocumentCatalogFolder[];
  pagination: DocumentCatalogPagination;
}

export async function getDocumentCatalog(
  municipalityId: number,
  query: DocumentCatalogQuery = {},
): Promise<DocumentCatalogResponse> {
  const params = new URLSearchParams();
  params.set("list", query.list ?? "research_assistant");
  params.set("page", String(query.page ?? 1));
  params.set("in_flight_page", String(query.inFlightPage ?? 1));
  if (query.q) params.set("q", query.q);
  if (query.recent) params.set("recent", "1");

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/document_catalog?${params}`, {
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch document catalog: ${response.statusText}`);
  }

  return response.json() as Promise<DocumentCatalogResponse>;
}

export async function updateDocumentCatalogPlacement(
  municipalityId: number,
  documentId: number,
  placement: DocumentCatalogPlacement,
): Promise<DocumentCatalogPlacement> {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  const response = await fetch(
    `/api/v1/municipalities/${municipalityId}/document_catalog/${documentId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify(placement),
    },
  );

  if (!response.ok) {
    let message = response.statusText;
    try {
      const errorData = await response.json();
      if (typeof errorData.error === "string" && errorData.error) {
        message = errorData.error;
      }
    } catch {
      // The status text is enough when the body is not JSON.
    }
    throw new Error(message);
  }

  return response.json() as Promise<DocumentCatalogPlacement>;
}

export async function uploadDocumentForMunicipality(municipalityId: number, docData: {
  name: string;
  notes?: string;
  file: File;
  file_center: boolean;
  connect: boolean;
  research_assistant: boolean;
  municipality_document_group_id?: string;
}) {
  const formData = new FormData();
  formData.append('document[name]', docData.name);
  if (docData.notes) {
    formData.append('document[notes]', docData.notes);
  }
  formData.append('document[file]', docData.file, docData.file.name);
  formData.append('document[file_center]', String(docData.file_center));
  formData.append('document[connect]', String(docData.connect));
  formData.append('document[research_assistant]', String(docData.research_assistant));
  if (docData.municipality_document_group_id) {
    formData.append('document[municipality_document_group_id]', docData.municipality_document_group_id);
  }
  formData.append('municipality_id', municipalityId.toString());

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/documents`, {
    method: 'POST',
    headers: {
      'X-CSRF-Token': csrfToken
    },
    body: formData
  });

  if (!response.ok) {
    let message = response.statusText;
    try {
      const errorData = await response.json();
      if (typeof errorData.error === "string" && errorData.error) {
        message = errorData.error;
      } else if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
        message = errorData.errors.join(", ");
      }
    } catch {
      message = response.statusText;
    }
    throw new Error(message || "Failed to upload document");
  }

  return response.json();
}

export function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

async function parseJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export function parseApiErrorMessage(data: unknown): string {
  if (data && typeof data === "object" && "message" in data) {
    const m = (data as { message: unknown }).message;
    if (typeof m === "string") return m;
  }
  return "";
}

export interface FeedbackApiResponse {
  status: string;
  message?: string;
  feedback_state?: MessageFeedbackState;
}

export async function submitStructuredMessageFeedback(
  messageId: number,
  body: { reasonCode: string; detail?: string | null },
): Promise<FeedbackApiResponse> {
  const response = await fetch("/api/v1/message_feedbacks", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({
      message_id: messageId,
      message_feedback: {
        message_id: messageId,
        reason_code: body.reasonCode,
        detail: body.detail ?? undefined,
      },
    }),
  });
  const data = (await parseJsonSafe(response)) as FeedbackApiResponse | null;
  if (!response.ok) {
    throw new Error(parseApiErrorMessage(data) || `Request failed (${response.status})`);
  }
  return (data || {}) as FeedbackApiResponse;
}

export async function submitMessageEvaluation(
  messageId: number,
  body: { evaluation: "helpful" | "unhelpful"; reasonCode?: string; detail?: string | null },
): Promise<FeedbackApiResponse> {
  const response = await fetch("/api/v1/message_evaluations", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({
      message_id: messageId,
      message_evaluation: {
        evaluation: body.evaluation,
        reason_code: body.reasonCode,
        detail: body.detail ?? undefined,
      },
    }),
  });
  const data = (await parseJsonSafe(response)) as FeedbackApiResponse | null;
  if (!response.ok) {
    throw new Error(parseApiErrorMessage(data) || `Request failed (${response.status})`);
  }
  return (data || {}) as FeedbackApiResponse;
}

export async function deleteMessageEvaluation(messageId: number): Promise<FeedbackApiResponse> {
  const response = await fetch(`/api/v1/message_evaluations/${messageId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({ message_id: messageId }),
  });
  const data = (await parseJsonSafe(response)) as FeedbackApiResponse | null;
  if (!response.ok) {
    throw new Error(parseApiErrorMessage(data) || `Request failed (${response.status})`);
  }
  return (data || {}) as FeedbackApiResponse;
}
